import React, { useCallback } from 'react'
import AsyncSelect from 'react-select/async'
import useSWR from 'swr'
import { api, fetchMatches } from '../services/api'
import { useDebouncedCallback } from '../hooks'
import selectStyles from '../styles/react-select.module.scss'

// Single-item async pickers: the control references exactly one item and the
// stored value is that item's raw id — so a picker searches the catalog to
// pick, and resolves the saved id back to a readable label when an existing
// record is reopened.

interface PickerOption {
  value: string
  label: string
}

export interface SingleItemPickerProps {
  value: string
  onChange: (id: string) => void
  /** Restrict the search to items ACE will render on this site's rails
   * (a tc_site.code) — the pin pickers pass the widget's site so a
   * merchandiser cannot pick content the rail refuses. The resolve side
   * stays ungated: a saved id keeps its label even after its content
   * loses the gate. */
  visibleOnSite?: string
}

const competitorName = (c: { name?: string } | null | undefined): string =>
  c?.name || 'TBD'

/** "Alcaraz vs Sinner — F · Roland-Garros" from a /matches list or detail row.
 *
 * The two endpoints name these differently and the picker hits both — the list
 * to search, the detail to resolve a saved id back to a label. The list
 * flattens competitors to `home_competitor`/`competition` siblings with a
 * `name` string (match_queries.fetch_matches_with_relations). The detail
 * (fetch_single_match) nests them INSIDE `match` as the tc/ext override pair
 * (`tc_home_competitor` wins over `ext_home_competitor`), and its competition
 * spells the name `tc_name`/`ext_name`. Reading only one shape renders a
 * selected match as "TBD vs TBD" — every fallback below is a shape one
 * endpoint actually returns, so keep them all. */
export const matchLabel = (row: any): string => {
  const m = row?.match ?? {}
  const home =
    row?.home_competitor ?? row?.effective_home_competitor ??
    m?.tc_home_competitor ?? m?.ext_home_competitor
  const away =
    row?.away_competitor ?? row?.effective_away_competitor ??
    m?.tc_away_competitor ?? m?.ext_away_competitor
  const competition = row?.competition ?? row?.effective_competition
  const competitionName =
    competition?.name ?? competition?.tc_name ?? competition?.ext_name
  const round = m?.effective_round ?? m?.ext_display_round
  const pair = `${competitorName(home)} vs ${competitorName(away)}`
  const detail = [round, competitionName].filter(Boolean).join(' · ')
  return detail ? `${pair} — ${detail}` : pair
}

/**
 * Shared shell: an AsyncSelect over `search`, plus an SWR lookup that turns a
 * saved id into its label. While that lookup is in flight the raw id shows, so
 * the control never renders blank and never silently drops a stored value.
 */
export const makeSingleItemPicker = (
  search: (input: string, visibleOnSite?: string) => Promise<PickerOption[]>,
  resolve: (id: string) => Promise<string>,
  swrKey: string,
  placeholder: string,
  noun: string,
): React.FC<SingleItemPickerProps> => {
  const Picker: React.FC<SingleItemPickerProps> = ({ value, onChange, visibleOnSite }) => {
    const { data: resolved } = useSWR(
      value ? [swrKey, value] : null,
      () => resolve(value),
      { revalidateOnFocus: false },
    )

    const loadOptions = useDebouncedCallback(
      useCallback(async (input: string): Promise<PickerOption[]> => {
        if (input.trim().length < 2) return []
        try {
          return await search(input.trim(), visibleOnSite)
        } catch (e) {
          console.error(`Failed to search ${noun}:`, e)
          return []
        }
      }, [visibleOnSite]),
      300,
    )

    return (
      <AsyncSelect<PickerOption, false>
        value={value ? { value, label: resolved ?? value } : null}
        onChange={(opt) => onChange(opt?.value ?? '')}
        loadOptions={loadOptions}
        isClearable
        // react-select keeps the async cache "until cacheOptions changes
        // value", so the gate is the cache key: switching sites drops results
        // the new site would refuse.
        cacheOptions={visibleOnSite ?? true}
        placeholder={placeholder}
        className={selectStyles.reactSelect}
        classNamePrefix="react-select"
        loadingMessage={() => `Loading ${noun}…`}
        noOptionsMessage={({ inputValue }) =>
          inputValue.trim().length < 2 ? placeholder : `No ${noun} found`
        }
      />
    )
  }
  return Picker
}

/** A saved match id resolved to the label above, exported with the SWR key the
 * picker resolves on so other callers share one cached request per match. */
export const MATCH_LABEL_SWR_KEY = 'match-select'

export const fetchMatchLabel = async (id: string): Promise<string> => {
  const resp = await api.get(`/matches/${id}`)
  const row = resp?.body ? JSON.parse(resp.body) : resp
  return matchLabel(row)
}

// GET /matches requires a date range and deliberately stays date-bounded — the
// query comment notes the bound is what stops the scan touching every match
// ever ingested. Merchandising picks are near-term, so the picker searches a
// window around today rather than the whole catalogue.
const MATCH_WINDOW_DAYS_BACK = 30
const MATCH_WINDOW_DAYS_FORWARD = 30

const isoDay = (offsetDays: number): string => {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  return d.toISOString().slice(0, 10)
}

export const MATCH_SEARCH_HINT =
  `Search by player name — matches from the last ${MATCH_WINDOW_DAYS_BACK} ` +
  `and next ${MATCH_WINDOW_DAYS_FORWARD} days`

export const MatchSelect = makeSingleItemPicker(
  async (input, visibleOnSite) => {
    const window = {
      start_date: isoDay(-MATCH_WINDOW_DAYS_BACK),
      end_date: isoDay(MATCH_WINDOW_DAYS_FORWARD),
      limit: 25,
      visible_on_site: visibleOnSite,
    }

    // /matches?search= matches ids only (tc_match_id, ext_event_id, pub content
    // id) — never competitor names. So a typed name is resolved to competitor
    // ids first and passed as player_ids, which is the same two-step the
    // matches list uses behind its player typeahead. Anything that resolves to
    // no competitor falls through to the id search, so pasting a match id
    // still works.
    const [players, teams] = await Promise.all([
      api.get('/players', { name: input, limit: 50 }).catch(() => null),
      api.get('/teams', { name: input, limit: 50 }).catch(() => null),
    ])
    const playerIds = [
      ...(players?.players || []).map((p: any) => p.tc_competitor_id),
      ...(teams?.teams || []).map((t: any) => t.tc_team_id),
    ].filter(Boolean)

    const resp = await fetchMatches(
      playerIds.length ? { ...window, player_ids: playerIds } : { ...window, search: input },
    )
    return (resp?.matches || []).map((row: any) => ({
      value: row.match.tc_match_id,
      label: matchLabel(row),
    }))
  },
  fetchMatchLabel,
  MATCH_LABEL_SWR_KEY,
  'Search by player name…',
  'matches',
)

export default MatchSelect
